/**
 * FavoriteButton - Botón reutilizable para agregar/quitar de favoritos
 *
 * Uso:
 * <FavoriteButton propertyId="abc123" size="md" showText={true} />
 */

import React, { useState, useEffect, useCallback } from 'react'
import { collection, query, where, getDocs, addDoc, deleteDoc, doc, Timestamp } from 'firebase/firestore'
import { db } from '../firebaseConfig'
import { useUser } from '../context/UserContext'
import Swal from 'sweetalert2'

interface FavoriteButtonProps {
  propertyId: string
  size?: 'sm' | 'md' | 'lg'
  showText?: boolean
  variant?: 'icon' | 'button' | 'pill'
  className?: string
  onToggle?: (isFavorite: boolean) => void
}

export default function FavoriteButton({
  propertyId,
  size = 'md',
  showText = false,
  variant = 'icon',
  className = '',
  onToggle
}: FavoriteButtonProps) {
  const { user } = useUser()
  const [isFavorite, setIsFavorite] = useState<boolean>(false)
  const [loading, setLoading] = useState<boolean>(false)
  const [favoritoDocId, setFavoritoDocId] = useState<string | null>(null)

  // Verificar si ya es favorito al montar
  useEffect(() => {
    const checkFavorite = async () => {
      if (!user?.id || !propertyId) return

      try {
        const q = query(
          collection(db, 'favoritos'),
          where('userId', '==', user.id),
          where('propertyId', '==', propertyId)
        )
        const snap = await getDocs(q)

        if (!snap.empty) {
          setIsFavorite(true)
          setFavoritoDocId(snap.docs[0].id)
        } else {
          setIsFavorite(false)
          setFavoritoDocId(null)
        }
      } catch (err) {
        console.error('Error verificando favorito:', err)
      }
    }

    checkFavorite()
  }, [user?.id, propertyId])

  // Toggle favorito
  const handleToggle = useCallback(async (e: React.MouseEvent) => {
    e.preventDefault()
    e.stopPropagation()

    // Si no hay usuario, mostrar mensaje
    if (!user?.id) {
      Swal.fire({
        icon: 'info',
        title: 'Inicia sesión',
        text: 'Debes iniciar sesión para guardar propiedades en favoritos',
        confirmButtonText: 'Entendido'
      })
      return
    }

    setLoading(true)

    try {
      if (isFavorite && favoritoDocId) {
        // Eliminar de favoritos
        await deleteDoc(doc(db, 'favoritos', favoritoDocId))
        setIsFavorite(false)
        setFavoritoDocId(null)
        onToggle?.(false)

        // Toast de confirmación
        const Toast = Swal.mixin({
          toast: true,
          position: 'bottom-end',
          showConfirmButton: false,
          timer: 2000,
          timerProgressBar: true
        })
        Toast.fire({
          icon: 'success',
          title: 'Eliminado de favoritos'
        })
      } else {
        // Agregar a favoritos
        const docRef = await addDoc(collection(db, 'favoritos'), {
          userId: user.id,
          propertyId: propertyId,
          fechaAgregado: Timestamp.now()
        })
        setIsFavorite(true)
        setFavoritoDocId(docRef.id)
        onToggle?.(true)

        // Toast de confirmación
        const Toast = Swal.mixin({
          toast: true,
          position: 'bottom-end',
          showConfirmButton: false,
          timer: 2000,
          timerProgressBar: true
        })
        Toast.fire({
          icon: 'success',
          title: 'Agregado a favoritos'
        })
      }
    } catch (err) {
      console.error('Error toggling favorito:', err)
      Swal.fire({
        icon: 'error',
        title: 'Error',
        text: 'No se pudo actualizar favoritos. Intenta de nuevo.'
      })
    } finally {
      setLoading(false)
    }
  }, [user?.id, propertyId, isFavorite, favoritoDocId, onToggle])

  // Tamaños
  const sizeClasses = {
    sm: 'btn-sm',
    md: '',
    lg: 'btn-lg'
  }

  const iconSizes = {
    sm: 'fs-6',
    md: 'fs-5',
    lg: 'fs-4'
  }

  // Estilos según variante
  if (variant === 'icon') {
    return (
      <button
        type="button"
        onClick={handleToggle}
        disabled={loading}
        className={`btn btn-link p-0 border-0 ${className}`}
        title={isFavorite ? 'Eliminar de favoritos' : 'Agregar a favoritos'}
        aria-label={isFavorite ? 'Eliminar de favoritos' : 'Agregar a favoritos'}
        aria-pressed={isFavorite}
      >
        {loading ? (
          <span className={`spinner-border spinner-border-sm ${iconSizes[size]}`} role="status" aria-hidden="true"></span>
        ) : (
          <i
            className={`fa-${isFavorite ? 'solid' : 'regular'} fa-heart ${iconSizes[size]} ${isFavorite ? 'text-danger' : 'text-muted'}`}
            style={{ transition: 'all 0.2s ease' }}
          ></i>
        )}
      </button>
    )
  }

  if (variant === 'pill') {
    return (
      <button
        type="button"
        onClick={handleToggle}
        disabled={loading}
        className={`btn ${isFavorite ? 'btn-danger' : 'btn-outline-danger'} rounded-pill ${sizeClasses[size]} ${className}`}
        aria-pressed={isFavorite}
      >
        {loading ? (
          <span className="spinner-border spinner-border-sm me-1" role="status" aria-hidden="true"></span>
        ) : (
          <i className={`fa-${isFavorite ? 'solid' : 'regular'} fa-heart me-1`}></i>
        )}
        {showText && (isFavorite ? 'Guardado' : 'Guardar')}
      </button>
    )
  }

  // variant === 'button'
  return (
    <button
      type="button"
      onClick={handleToggle}
      disabled={loading}
      className={`btn ${isFavorite ? 'btn-danger' : 'btn-outline-secondary'} ${sizeClasses[size]} ${className}`}
      aria-pressed={isFavorite}
    >
      {loading ? (
        <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
      ) : (
        <i className={`fa-${isFavorite ? 'solid' : 'regular'} fa-heart ${showText ? 'me-2' : ''}`}></i>
      )}
      {showText && (isFavorite ? 'En favoritos' : 'Agregar a favoritos')}
    </button>
  )
}
